import React, { useState, useEffect } from 'react';
import NavBars from '../Sidebar/Sidebar';
import './protected.css';
import Calendar from 'react-calendar';
import { addMonths, setHours, setMinutes, differenceInMinutes, isSameDay, format, isValid } from 'date-fns';
import { db } from '../../firebase'; 
import { collection, addDoc, doc, getDocs, deleteDoc, query,where } from 'firebase/firestore';
import { UserAuth } from '../../authContext';

const Booking = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [duration, setDuration] = useState(1); 
  const [availableTimes, setAvailableTimes] = useState([]);
  
  const { user } = UserAuth();
  const MAX_END_HOUR = 18; 

  // Handle date selection and fetch available times
  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchAvailableTimes(date);
  };

  // Handle time selection
  const handleTimeChange = (time) => {
    setSelectedTime(time);
    setDuration(1); // Reset duration to 1 hour when time changes
};

  // Handle duration change
  const handleDurationChange = (event) => {
    const newDuration = parseInt(event.target.value, 10);
    const endTime = calculateEndTime(selectedTime, newDuration);

    if (endTime && endTime.getHours() >= MAX_END_HOUR) {
      const availableMinutes = differenceInMinutes(
        setHours(setMinutes(new Date(selectedDate), 0), MAX_END_HOUR),
        new Date(selectedTime)
      );
      setDuration(Math.floor(availableMinutes / 60)); 
    } else {
      setDuration(newDuration);
    }
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
  
    if (!selectedDate || !selectedTime || !duration) {
        console.error("Please select a valid date, time, and duration");
        return;
    }
  
    const endTime = calculateEndTime(selectedTime, duration);
  
    // Format the confirmation message
    const confirmationMessage = `
      Please confirm your booking:
      \nDate: ${format(selectedDate, 'PP')}
      \nStart Time: ${selectedTime}
      \nEnd Time: ${format(endTime, 'p')}
    `;
  
    // Show confirmation alert
    const isConfirmed = window.confirm(confirmationMessage);
  
    if (isConfirmed) {
      confirmBooking(); // Call the final booking submission function if user confirms
    }
  };
  
  const confirmBooking = async () => {
    const [time, period] = selectedTime.split(' ');
    const [hoursStr, minutesStr] = time.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10) || 0;
  
    let hours24 = hours;
    if (period === 'PM' && hours !== 12) {
        hours24 = hours + 12;
    } else if (period === 'AM' && hours === 12) {
        hours24 = 0;
    }
  
    const validStartTime = setMinutes(setHours(new Date(selectedDate), hours24), minutes);
  
    const endTime = calculateEndTime(selectedTime, duration);
  
    const bookingDetails = {
      startDate: selectedDate.toISOString(),
      startTime: validStartTime.toISOString(),
      endTime: endTime.toISOString(),
      duration: duration,
      username: user?.email,
      status: "pending"
    };
  
    try {
      const bookingsCollection = collection(db, 'bookings');
      await addDoc(bookingsCollection, bookingDetails);
      console.log('Booking successfully added to Firestore:', bookingDetails);
    } catch (error) {
      console.error('Error adding booking to Firestore:', error);
    }
  
    // Reset form after submission
    setSelectedDate(new Date());
    setSelectedTime(null);
    setDuration(1);
  };
  

  // Fetch available times (simulated data here)
  const fetchAvailableTimes = (date) => {
    const simulatedAvailableTimes = [
      '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', 
      '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
    ];
    setAvailableTimes(simulatedAvailableTimes);
  };

  
  

  const calculateEndTime = (selectedTime, durationInHours) => {
    if (!selectedTime || !selectedDate) return null;

    const [time, period] = selectedTime.split(' ');
    const [hoursStr, minutesStr] = time.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10) || 0;

    let hours24 = hours;
    if (period === 'PM' && hours !== 12) {
        hours24 = hours + 12; 
    } else if (period === 'AM' && hours === 12) {
        hours24 = 0; 
    }

    const start = setMinutes(setHours(new Date(selectedDate), hours24), minutes);
    const end = new Date(start.getTime() + durationInHours * 60 * 60 * 1000);

    if (end.getHours() >= MAX_END_HOUR) {
        return setMinutes(setHours(new Date(selectedDate), MAX_END_HOUR), 0);
    }

    return end;
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return ''; 
    return `${format(date, 'PP')} ${time}`;
  };

  const BookingForm = () => {
    const today = new Date();
    const minDate = today;
    const maxDate = addMonths(today, 6);
    const endTime = selectedTime ? calculateEndTime(selectedTime, duration) : null;

    return (
      <div >
        <form onSubmit={handleSubmit} className="booking-form">
          <h2 className="section-heading">Select a day</h2>
          <Calendar
            onChange={handleDateChange}
            value={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            tileClassName={({ date }) => {
              const currentDate = new Date();
              if (date < currentDate) return 'disabled-day';
              else if (isSameDay(date, selectedDate)) return 'selected-day';
            }}
          />
          
          <div className="time-duration-container">
            <h2 className="section-heading">Select the time and duration</h2>
            <select value={selectedTime} onChange={(e) => handleTimeChange(e.target.value)} className="time-select">
              <option value="">Select Start Time</option>
              {availableTimes.map((time, index) => (
                <option key={index} value={time}>
                  {time}
                </option>
              ))}
            </select>

            <input
              type="number"
              value={duration}
              onChange={handleDurationChange}
              min="1"
              max="10"
              className="duration-input"
            />
          </div>

          <div className="button-container">
            <button 
              type="submit" 
              className="submit-button"
              disabled={!selectedDate || !selectedTime || !duration}>
              Book Lab
            </button>
          </div>
        </form>
      </div>
    );
  };

  const BookingRequests = () => {
    const { user } = UserAuth();
    const [bookingRequests, setBookingRequests] = useState([]);
    const [loading, setLoading] = useState(true);
  
    useEffect(() => {
      const fetchBookings = async () => {
        if (!user) {
          setLoading(false);
          return;
        }
  
        setLoading(true); // Set loading to true when fetching starts
  
        const bookingsCollection = collection(db, 'bookings');
        const userQuery = query(bookingsCollection, where('username', '==', user.email));
  
        try {
          const snapshot = await getDocs(userQuery);
          const bookings = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));
          setBookingRequests(bookings);
        } catch (error) {
          console.error('Error fetching bookings:', error);
        } finally {
          setLoading(false); // Set loading to false when fetching completes
        }
      };
  
      fetchBookings();
    }, [user]);
  
    const handleCancel = async (requestId) => {
      const isConfirmed = window.confirm('Are you sure you want to cancel this booking?');
  
      if (isConfirmed) {
        try {
          const bookingDocRef = doc(db, 'bookings', requestId);
          await deleteDoc(bookingDocRef);
          setBookingRequests(prevRequests => prevRequests.filter(request => request.id !== requestId));
          console.log('Booking successfully canceled:', requestId);
        } catch (error) {
          console.error('Error canceling booking:', error);
        }
      }
    };
  
    return (
      <div>
        <h2 className="section-heading">Your Pending Booking Requests</h2>
        <div className="booking-requests-container">
          {loading ? (
            <p>Loading...</p>
          ) : (
            bookingRequests.length > 0 ? (
              bookingRequests.filter(request => request.status === 'pending').map((request) => {
                const startTime = new Date(request.startTime);
                const endTime = new Date(request.endTime);
  
                return (
                  <div key={request.id} className="item">
                    <p><strong>Date:</strong> {format(new Date(request.startDate), 'PP')}</p>
                    <p><strong>Start Time:</strong> {format(startTime, 'p')}</p>
                    <p><strong>End Time:</strong> {format(endTime, 'p')}</p>
                    <button className="b" onClick={() => handleCancel(request.id)}>Cancel</button>
                  </div>
                );
              })
            ) : (
              <p>No pending requests found</p>
            )
          )}
        </div>
      </div>
    );
  };
  
  
  
  
  
  
  

  return (
    <div className="booking-page">
      <div className="content grid-container">
        <div className="left-column">
          <BookingForm />
        </div>
        <div className="right-column">
          <BookingRequests />
        </div>
      </div>
      <NavBars />
    </div>
  );

};

export default Booking;
